import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { AuthService } from '../services/AuthService';
import { LoginRequest } from '../types/auth';
import { identifyUser, resetUser, analytics } from '../services/analytics';

type AuthErrorSource = 'credentials' | 'google' | null;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  errorSource: AuthErrorSource;
  login: (credentials: LoginRequest) => Promise<void>;
  loginWithGoogle: (email: string, googleId: string, name?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorSource, setErrorSource] = useState<AuthErrorSource>(null);
  const authService = new AuthService();

  const getAccessToken = (): string | null => {
    return localStorage.getItem('accessToken');
  };

  const isJwtExpired = (token: string): boolean => {
    try {
      const payloadBase64 = token.split('.')[1];
      const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);
      if (!payload || typeof payload.exp !== 'number') return true;
      const expiresAtMs = payload.exp * 1000;
      return Date.now() >= expiresAtMs;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    // Check if user is already logged in
    const initAuth = async () => {
      const token = getAccessToken();
      const userStr = localStorage.getItem('user');

      if (token && !isJwtExpired(token) && userStr) {
        try {
          setUser(JSON.parse(userStr));
        } catch (error) {
          console.error('Failed to parse user:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      } else {
        // Stale or missing token → ensure clean state
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        setUser(null);
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    setIsLoading(true);
    setError(null);
    setErrorSource(null);
    try {
      const response = await authService.login(credentials);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('userId', response.user.id);
      setUser(response.user);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      setErrorSource('credentials');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (email: string, googleId: string, name?: string) => {
    setIsLoading(true);
    setError(null);
    setErrorSource(null);
    try {
      const response = await authService.loginWithGoogle(email, googleId, name);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('userId', response.user.id);
      setUser(response.user);
      // Track login in analytics
      identifyUser(response.user.id, response.user.email, `${response.user.firstName} ${response.user.lastName}`);
      analytics.login(response.user.email, 'google');
    } catch (error: any) {
      console.error('Google login failed:', error?.response?.data || error);
      setError('Google login failed. Please try again.');
      setErrorSource('google');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
    setErrorSource(null);
  };

  const logout = () => {
    // Track logout in analytics
    analytics.logout();
    resetUser();

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    setUser(null);
    authService.clearCache();
  };

  const refreshUser = async () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && (() => {
      const token = getAccessToken();
      return !!token && !isJwtExpired(token);
    })(),
    isLoading,
    error,
    errorSource,
    login,
    loginWithGoogle,
    logout,
    refreshUser,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
